import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import '../../styles/statsCard.css';
import { useSales } from '../../hooks/useSales';

const TotalSalesStats = ({ variant = "daily" }) => {
  const { sales = [] } = useSales();
  const now = useMemo(() => new Date(), []);

  const totalItemsSold = useMemo(() =>
    sales.reduce((sum, sale) =>
      sum + (sale.items?.reduce((s, item) => s + (item.quantity || 0), 0) || 0), 0
    ), [sales]);

  const monthlyItemsSold = useMemo(() =>
    sales
      .filter(sale => {
        const created = new Date(sale.createdAt);
        return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
      })
      .reduce((sum, sale) =>
        sum + (sale.items?.reduce((s, item) => s + (item.quantity || 0), 0) || 0), 0
      ), [sales, now]);

  const dailyItemsSold = useMemo(() => {
    const todayStr = now.toISOString().split("T")[0];
    return sales
      .filter(sale => sale.createdAt.split("T")[0] === todayStr)
      .reduce((sum, sale) =>
        sum + (sale.items?.reduce((s, item) => s + (item.quantity || 0), 0) || 0), 0
      );
  }, [sales, now]);

  let itemsSold = 0;
  let previousItemsSold = 0;
  let title = "";

  switch (variant) {
    case "daily":
      itemsSold = dailyItemsSold;
      const yesterdayStr = new Date(now.getTime() - 86400000).toISOString().split("T")[0];
      previousItemsSold = sales
        .filter(sale => sale.createdAt.split("T")[0] === yesterdayStr)
        .reduce((sum, sale) =>
          sum + (sale.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0), 0
        );
      title = "Today's Sales";
      break;
    case "monthly":
      itemsSold = monthlyItemsSold;
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      previousItemsSold = sales
        .filter(sale => {
          const created = new Date(sale.createdAt);
          return created.getMonth() === lastMonth.getMonth() && created.getFullYear() === lastMonth.getFullYear();
        })
        .reduce((sum, sale) =>
          sum + (sale.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0), 0
        );
      title = "Monthly Sales";
      break;
    case "total":
    default:
      itemsSold = totalItemsSold;
      previousItemsSold = 0;
      title = "Total Sales";
  }

  // Cap at 100%
  const rawPercentage = previousItemsSold
    ? ((itemsSold - previousItemsSold) / previousItemsSold) * 100
    : itemsSold > 0 ? 100 : 0;

  const percentage = Math.min(Math.abs(rawPercentage), 100) * (rawPercentage < 0 ? -1 : 1);
  const isPositive = percentage >= 0;

  return (
    <div className="stat-card sales">
      <h3>{title}</h3>
      <div className="value">{itemsSold}</div>
      {variant !== "total" && (
        <div className={`change ${isPositive ? "positive" : "negative"}`}>
          <FontAwesomeIcon icon={isPositive ? "arrow-up" : "arrow-down"} className="icon" />
          <span>{Math.abs(percentage).toFixed(1)}% {isPositive ? "increase" : "decrease"}</span>
        </div>
      )}
    </div>
  );
};

export default TotalSalesStats;
